<script setup lang="ts">
    import { getCodeSetLink } from '@/shared/helpers/routing';
    import { shortDate } from '@/formatters';
    import type { CodeSetStatisticsReadModel } from '@/shared/helpers/api';

    withDefaults(defineProps<{
        details: CodeSetStatisticsReadModel[];
        loadingDetails: boolean;
        tableHeight: string;
        isSearchable?: boolean;
    }>(), {
        isSearchable: true
    });

    function rowCssClass(row: CodeSetStatisticsReadModel): string {
        return (row.lockedForDelete && row.isReleaseReady) ? '' : 'not-release-ready';
    }
</script>

<template>
    <b-table
        :data="details"
        :default-sort="['codeSetDescription', 'asc']"
        :striped="false"
        :narrowed="true"
        :hoverable="false"
        :loading="loadingDetails"
        :sticky-header="true"
        :height="tableHeight"
        marginless
        :row-class="rowCssClass">
        <b-table-column
            v-slot="table"
            field="codeSetDescription"
            label="Set"
            :searchable="isSearchable"
            sortable>
            <router-link :to="getCodeSetLink(table.row.codeSetName)">
                {{ table.row.codeSetDescription }}
            </router-link>
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="lockedForDelete"
            label=""
            sortable>
            <b-tooltip :label="table.row.lockedForDelete ? 'Locked for delete' : 'Open for delete'">
                <b-icon
                    size="is-small"
                    type="is-info"
                    :icon="table.row.lockedForDelete ? 'lock' : 'lock-off'" />
            </b-tooltip>
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="isReleaseReady"
            label=""
            sortable>
            <b-tooltip :label="table.row.isReleaseReady ? 'Released' : 'Not ready for release'">
                <b-icon
                    size="is-small"
                    type="is-info"
                    :icon="table.row.isReleaseReady ? 'cloud' : 'alert'" />
            </b-tooltip>
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="statisticsUpdatedDate"
            label="Updated"
            sortable>
            <b-tooltip
                :label="table.row.statisticsUpdatedDate"
                position="is-right">
                {{ shortDate(table.row.statisticsUpdatedDate) }}
            </b-tooltip>
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="codeCount"
            label="#codes"
            numeric
            sortable>
            {{ table.row.codeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="validCodeCount"
            label="#valid"
            numeric
            sortable>
            {{ table.row.validCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            label="#invalid"
            numeric>
            {{ table.row.codeCount - table.row.validCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidCodeCount"
            label="#codes STID"
            numeric
            sortable>
            {{ table.row.stidCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidValidCodeCount"
            label="#valid STID"
            numeric
            sortable>
            {{ table.row.stidValidCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidMissingInStidCodeCount"
            label="#missing STID"
            numeric
            sortable>
            {{ table.row.stidMissingInStidCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidMissingInCommonLibCodeCount"
            label="#missing CL"
            numeric
            sortable>
            {{ table.row.stidMissingInCommonLibCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidInBothButDifferentCodeCount"
            label="#diff w/STID"
            numeric
            sortable>
            {{ table.row.stidInBothButDifferentCodeCount }}
        </b-table-column>
        <b-table-column
            v-slot="table"
            field="stidSyncDate"
            label="STID sync"
            sortable>
            <b-tooltip
                :label="table.row.stidSyncDate"
                position="is-left">
                {{ shortDate(table.row.stidSyncDate) }}
            </b-tooltip>
        </b-table-column>
    </b-table>
</template>

<style scoped>

</style>
